import { useEffect, useState } from "react";

import {
  getUserPresence,
  getUserStatus,
  readMyStatus,
  setMyStatus,
  subscribePresence,
  subscribeMyStatus,
  type Client,
  type PresenceSnapshot,
  type UserStatus,
} from "~/lib/matrix";

export type { UserStatus } from "~/lib/matrix";

const MAX_TIMEOUT_MS = 2_147_483_647;
const EMOJI_GRAPHEME =
  "\\p{Extended_Pictographic}(?:\\uFE0F)?(?:\\u200D\\p{Extended_Pictographic}(?:\\uFE0F)?)*";
const EMOJI_STATUS_RE = new RegExp(`^(${EMOJI_GRAPHEME})\\s+(.+)$`, "u");
const EMOJI_ONLY_RE = new RegExp(`^${EMOJI_GRAPHEME}$`, "u");

export function isEmoji(value: string): boolean {
  return EMOJI_ONLY_RE.test(value.trim());
}

export function fallbackFromPresence(snapshot: PresenceSnapshot): UserStatus | null {
  const msg = snapshot.statusMsg?.trim();
  if (!msg) return null;
  const match = EMOJI_STATUS_RE.exec(msg);
  if (!match) return null;
  const emoji = match[1];
  const message = match[2]?.trim();
  if (!emoji || !message) return null;
  return { emoji, message, expiresAt: null };
}

function safeDelay(expiresAt: number): number {
  return Math.min(Math.max(0, expiresAt - Date.now()), MAX_TIMEOUT_MS);
}

export function useMyStatus(client: Client | null) {
  const [status, setStatusState] = useState<UserStatus | null>(null);

  useEffect(() => {
    if (!client) {
      setStatusState(null);
      return;
    }
    let cancelled = false;
    const unsub = subscribeMyStatus(client, (next) => {
      setStatusState(next);
    });
    void (async () => {
      const current = await readMyStatus(client);
      if (!cancelled) setStatusState(current);
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [client]);

  useEffect(() => {
    if (!client || !status?.expiresAt) return;
    const check = () => {
      if (status.expiresAt && status.expiresAt <= Date.now()) {
        void setMyStatus(client, null).catch((err) => console.warn("clear status failed", err));
      }
    };
    check();
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [client, status]);

  const setStatus = async (next: UserStatus | null) => {
    if (!client) return;
    await setMyStatus(client, next);
  };

  return { status, setStatus };
}

export function useUserStatus(client: Client | null, userId: string | null): UserStatus | null {
  const [status, setStatus] = useState<UserStatus | null>(null);

  useEffect(() => {
    if (!client || !userId) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    let sawPresenceStatus = false;
    setStatus(null);
    const unsubPresence = subscribePresence(client, userId, (presence) => {
      if ("statusMsg" in presence) {
        sawPresenceStatus = true;
        setStatus(fallbackFromPresence(presence));
      }
    });
    void (async () => {
      try {
        const next = await getUserStatus(client, userId);
        if (!cancelled && !sawPresenceStatus) {
          setStatus(next ?? fallbackFromPresence(getUserPresence(client, userId)));
        }
      } catch {
        if (!cancelled) setStatus(fallbackFromPresence(getUserPresence(client, userId)));
      }
    })();
    return () => {
      cancelled = true;
      unsubPresence();
    };
  }, [client, userId]);

  useEffect(() => {
    if (!status?.expiresAt) return;
    const id = window.setTimeout(() => setStatus(null), safeDelay(status.expiresAt));
    return () => window.clearTimeout(id);
  }, [status?.expiresAt]);

  return status;
}
